<script setup lang="ts">
/**
 * El logotipo de Chuy: el casco de obra, con bigote.
 *
 * Una sola versión, la misma en todos lados: el cuadro con el degradado de la
 * casa (coral → durazno → verde menta) y el dibujo en blanco encima.
 *
 * Tres cosas hacen que el casco y el bigote no se confundan, que era el
 * problema de la versión anterior:
 *   • el ala del casco es bastante más ancha que el bigote (16.6 contra 10.7 de
 *     medio ancho), así que a simple vista son dos formas distintas y no dos
 *     rayas iguales;
 *   • entre el ala y el bigote hay un hueco de tres unidades, que a 24 px sigue
 *     leyéndose;
 *   • el bigote trae una muesca marcada en medio (donde iría la nariz) y las
 *     puntas curveadas hacia arriba.
 *
 * Las ranuras del domo no se pintan: se rellenan con el mismo degradado del
 * cuadro, así que dejan ver el fondo sin recortar nada.
 *
 * **Estas mismas medidas viven en `bin/icono.py`**, que dibuja el icono de la
 * app: si le mueves aquí, muévele allá y vuelve a correr `python3 bin/icono.py`
 * y `npm run app:crear`.
 */
import { useId } from "vue";

withDefaults(defineProps<{ size?: number }>(), { size: 32 });

const gradientId = `chuy-degradado-${useId()}`;
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Chuy"
    class="shrink-0"
  >
    <defs>
      <linearGradient :id="gradientId" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="var(--brand-coral)" />
        <stop offset="52%" stop-color="#f7dac8" />
        <stop offset="100%" stop-color="var(--brand-mint)" />
      </linearGradient>
    </defs>

    <rect width="48" height="48" rx="13" :fill="`url(#${gradientId})`" />

    <g fill="#ffffff">
      <!-- El domo del casco. -->
      <path d="M14.2 21.4a9.8 9.8 0 0 1 19.6 0Z" />
      <!-- El ala, ancha y delgadita: es lo que lo hace casco y no gorra. -->
      <ellipse cx="24" cy="22.6" rx="16.6" ry="1.95" />
      <!-- El bigote: más corto que el ala, grueso en medio y con las puntas arriba. -->
      <path
        d="M24 31.4C22 28 18.4 26.8 15 28.4C13.6 27.2 12.6 28 13.2 29.4C13 33 17.8 36 24 36C30.2 36 35 33 34.8 29.4C35.4 28 34.4 27.2 33 28.4C29.6 26.8 26 28 24 31.4Z"
      />
    </g>

    <!-- Las dos ranuras del domo, rellenas con el fondo. -->
    <g :fill="`url(#${gradientId})`">
      <rect x="18.55" y="14.2" width="2.1" height="7.2" rx="1.05" />
      <rect x="27.35" y="14.2" width="2.1" height="7.2" rx="1.05" />
    </g>
  </svg>
</template>
